import type { Answers, Question, Section } from "../types";
import { isAnswered, isQuestionApplicable } from "./questions";

export interface ResolvedAnswer {
  id: string;
  label: string;
  type: Question["type"];
  required: boolean;
  /** Raw stored value — option ids, text, number, or null when unanswered. */
  value: string | string[] | number | null;
  /** Human-readable rendering of `value`; "–" when unanswered. */
  valueLabel: string;
  /** Extra decision context attached to the chosen option(s) in the form. */
  sublabels?: string[];
}

export interface ResolvedSection {
  index: string;
  id: string;
  title: string;
  description: string;
  answers: ResolvedAnswer[];
}

function labelFor(question: Question, value: string): string {
  return question.options?.find((option) => option.value === value)?.label ?? value;
}

function sublabelsFor(question: Question, value: string | string[]): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((entry) => question.options?.find((option) => option.value === entry)?.sublabel)
    .filter((entry): entry is string => Boolean(entry));
}

export function resolveAnswer(question: Question, raw: Answers[string]): ResolvedAnswer {
  const answered = isAnswered(raw);
  const base = {
    id: question.id,
    label: question.label,
    type: question.type,
    required: Boolean(question.required),
  };

  if (!answered) {
    return { ...base, value: null, valueLabel: "–" };
  }

  if (Array.isArray(raw)) {
    return {
      ...base,
      value: raw,
      valueLabel: raw.map((entry) => labelFor(question, entry)).join(", "),
      sublabels: sublabelsFor(question, raw),
    };
  }

  if (typeof raw === "number") {
    return { ...base, value: raw, valueLabel: String(raw) };
  }

  const asString = String(raw);
  return {
    ...base,
    value: asString,
    valueLabel: question.options ? labelFor(question, asString) : asString,
    ...(question.options ? { sublabels: sublabelsFor(question, asString) } : {}),
  };
}

/** Resolved, human-readable view of the whole briefing — the payload a reader (or an LLM) needs without the form. */
export function buildSelections(sections: Section[], answers: Answers): ResolvedSection[] {
  return sections.map((section) => ({
    index: section.index,
    id: section.id,
    title: section.title,
    description: section.description,
    answers: section.questions
      .filter((question) => isQuestionApplicable(question, answers))
      .map((question) => resolveAnswer(question, answers[question.id])),
  }));
}

/** Machine-readable definition of the questionnaire itself, so the export explains its own keys. */
export function buildSchemaBlock(sections: Section[]) {
  return {
    description:
      "Definition des Fragebogens. `answers` referenziert diese question-ids; `selections` enthält dieselben Angaben bereits aufgelöst.",
    sections: sections.map((section) => ({
      index: section.index,
      id: section.id,
      title: section.title,
      description: section.description,
      questions: section.questions.map((question) => ({
        id: question.id,
        label: question.label,
        type: question.type,
        required: Boolean(question.required),
        ...(question.help ? { help: question.help } : {}),
        ...(question.dependsOn ? { dependsOn: question.dependsOn } : {}),
        ...(question.options
          ? {
              options: question.options.map((option) => ({
                value: option.value,
                label: option.label,
                ...(option.sublabel ? { sublabel: option.sublabel } : {}),
              })),
            }
          : {}),
      })),
    })),
  };
}
